import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';

export interface EASScheme {
  code: string;
  name: string;
  description: string;
  format: string;
  example: string;
}

export const EAS_SCHEMES: EASScheme[] = [
  {
    code: 'EM',
    name: 'Email',
    description: 'Email address for electronic invoicing',
    format: 'user@domain.com',
    example: 'invoice@company.com'
  },
  {
    code: '0204',
    name: 'Leitweg-ID',
    description: 'German public sector routing identifier',
    format: 'xx-xxxxxx-xx',
    example: '99-12345-67'
  },
  {
    code: '0007',
    name: 'Swedish Legal Entity',
    description: 'Swedish organization number',
    format: 'NNNNNNNNNN',
    example: '5562345678'
  },
  {
    code: '0060',
    name: 'DUNS Number',
    description: 'Dun & Bradstreet D-U-N-S Number',
    format: 'NNNNNNNNN',
    example: '123456789'
  },
  {
    code: '0088',
    name: 'EAN Location Code',
    description: 'Global Location Number (GLN)',
    format: 'NNNNNNNNNNNNN',
    example: '1234567890123'
  },
  {
    code: '0192',
    name: 'Norwegian Business Registry',
    description: 'Norwegian Enhetsregisteret number',
    format: 'NNNNNNNNN',
    example: '123456789'
  },
  {
    code: '0196',
    name: 'Icelandic Legal ID',
    description: 'Icelandic national identification number',
    format: 'NNNNNNNNNN',
    example: '1234567890'
  },
  {
    code: '0199',
    name: 'Legal Entity Identifier',
    description: 'ISO 17442 Legal Entity Identifier (LEI)',
    format: 'XXXXXXXXXXXXXXXXXXXX',
    example: '1234567890ABCDEFGH12'
  },
  {
    code: '0209',
    name: 'GS1 Identification',
    description: 'GS1 identification keys',
    format: 'Variable length',
    example: '1234567890123'
  },
  {
    code: '0212',
    name: 'Finnish Organization',
    description: 'Finnish Organization Identifier',
    format: 'NNNNNNN-N',
    example: '1234567-8'
  },
  {
    code: '9925',
    name: 'Belgium VAT',
    description: 'Belgium VAT identification number',
    format: 'BENNNNNNNNN',
    example: 'BE0123456789'
  },
  {
    code: '9932',
    name: 'UK VAT',
    description: 'United Kingdom VAT registration number',
    format: 'GBNNNNNNNNN',
    example: 'GB123456789'
  },
  {
    code: '9959',
    name: 'US EIN',
    description: 'US Employer Identification Number',
    format: 'NN-NNNNNNN',
    example: '12-3456789'
  }
];

interface EASSchemeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  showGermanNames?: boolean;
  className?: string;
  'data-testid'?: string;
}

const EASSchemeSelector: React.FC<EASSchemeSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select electronic address scheme...',
  disabled = false,
  error = false,
  showGermanNames = false,
  className = '',
  'data-testid': testId
}) => {
  // const { t } = useTranslation(); // Currently unused

  const inputClasses = `
    px-3 py-2 border rounded-md transition-colors text-ellipsis overflow-hidden
    ${error ? 'border-error focus:border-error' : 'border-border-primary focus:border-brand-primary'}
    ${disabled ? 'bg-bg-tertiary cursor-not-allowed' : 'bg-bg-primary'}
    ${className}
  `;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={inputClasses}
      data-testid={testId}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {EAS_SCHEMES.map((scheme) => (
        <option key={scheme.code} value={scheme.code}>
          {scheme.name} ({scheme.code})
        </option>
      ))}
    </select>
  );
};

export default EASSchemeSelector;